/**
 * portal 面 API のレスポンス型。
 *
 * **ここは「API が実際に返す形」の写しである**。以前は「UI から見た期待形」を
 * 先に書いてよいことにしていたが、その結果 API 実装と独立に育ってしまい、
 * 実在しないフィールドを読む画面（`usage.storage`、`subscription.monthlyPrice`、
 * `token.perms` など）が大量に残った。UI にとって都合の良い形が必要な場合は
 * **この型を曲げず**、`lib/dns-records.ts` のような変換アダプタを境界に 1 つ置く。
 *
 * 検証方法: `curl` で実レスポンスを取り、キーと型を突き合わせる。
 * 命名は API のフィールド名そのまま（camelCase）。状態値の語彙は
 * `@mail-sv/shared` の enum を必ず参照し、ここで文字列リテラルを再定義しない。
 */
import type {
  DnsCheckResult,
  DomainStatus,
  InvoiceStatus,
  JobStatus,
  MailboxStatus,
  OrgAiPolicy,
  OrganizationStatus,
  OrgPrivacyPolicy,
  OrgRole,
  PrivateAddressSummary,
  PrivateAddressView,
  SendingStreamKind,
  SendingStreamStatus,
  SignupChallengeMode,
  SignupFormStatus,
  SpamAction,
  SubscriptionStatus,
} from '@mail-sv/shared';

/* ------------------------------------------------------------------ 共通形 */

/** cursor ページング（§4.3 既定）。総件数は返らない。 */
export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

/** 件数を返してよい小規模一覧（§4.3 の例外リスト: domains / members / api-tokens）。 */
export interface CountedList<T> {
  items: T[];
  total: number;
}

/** 長時間処理の受理応答。 */
export interface JobAccepted {
  jobId: string;
}

/**
 * `GET /portal/jobs/:jobId`（`toPublicJobView()`）。
 * `userMessage` は投入時に付けていなければ null になる。
 */
export interface JobProgress {
  jobId: string;
  kind: string;
  state: JobStatus;
  /** 0..100 */
  progress: number;
  /** 画面にそのまま出せる日本語。内部エラー本文は入らない。 */
  userMessage: string | null;
  createdAt: string;
  finishedAt: string | null;
  /**
   * 続きが外部サイトにあるジョブの遷移先（決済の Checkout など）。
   *
   * ワーカーは `provisioning_jobs.output.redirectUrl` に書き戻す
   * （`apps/worker/src/workers/billing.ts`）。**契約者面のジョブ参照が
   * `output` を許可リストで返すようになるまで、ここは常に undefined。**
   * 画面はその前提で「あればボタンを出す」形にしてある
   * （`components/job-progress.tsx`）。
   *
   * 参照側の実装は `apps/api/src/routes/portal/misc.ts` の select と
   * `apps/api/src/services/provisioning.ts` の `toPublicJobView()` の 2 か所。
   */
  redirectUrl?: string | null;
  redirectExpiresAt?: string | null;
}

/**
 * `domains.dns_status`（`@mail-sv/db` の `DNS_STATUS`）。
 *
 * 語彙の出典は DB の pgEnum だが、`@mail-sv/db` は Node 専用パッケージなので
 * SPA からは import できない。ここでの再定義はその制約によるもので、
 * 値を勝手に決めているわけではない（`verified` / `failed` のような別語彙を
 * 画面が使っていたのが以前の不整合の原因）。
 */
export type DnsStatus = 'unknown' | 'ok' | 'partial' | 'broken';

/* -------------------------------------------------------------- 認証・自分 */

/** `GET /api/v1/auth/portal/me`。 */
export interface PortalUser {
  id: string;
  email: string;
  displayName: string;
  role: OrgRole;
  organizationId: string;
  /** 有効な権限コード（`org.mailbox.write` など）。 */
  permissions: string[];
  mfaEnabled: boolean;
  recoveryCodesRemaining: number;
  mustChangePassword: boolean;
  /** 認証手段（`pwd` / `otp`）。fresh-auth の判定に使う。 */
  amr: string[];
  /** 複数組織に所属している担当者向け。1 件なら組織切替 UI を出さない。 */
  organizations: Array<{
    organizationId: string;
    role: OrgRole;
    slug: string;
    displayName: string;
  }>;
  /**
   * 運営者による代理ログイン中は true。UI に常時バナーを出す。
   * `/me` はまだこのフィールドを返さない（代理ログインの実装が ops 面のみ）ので
   * optional のままにしてある。返るようになったら必須にすること。
   */
  impersonated?: boolean;
}

/** ログイン成功時の応答。`user` は `/me` より項目が少ない。 */
export interface LoginSuccess {
  accessToken: string;
  expiresIn: number;
  user: Pick<PortalUser, 'id' | 'email' | 'displayName' | 'role' | 'organizationId'> &
    Partial<PortalUser>;
  mfaRequired?: false;
}

export interface LoginMfaChallenge {
  mfaRequired: true;
  mfaTicket: string;
  methods: Array<'totp' | 'recovery'>;
}

export type LoginResponse = LoginSuccess | LoginMfaChallenge;

export function isMfaChallenge(res: LoginResponse): res is LoginMfaChallenge {
  return 'mfaRequired' in res && res.mfaRequired === true;
}

/** `GET /api/v1/public/config` — dev ログインボタンの可否をサーバ真実から得る。 */
export interface PublicConfig {
  serviceName: string;
  environment: string;
  serviceBaseDomain: string;
  webmailUrl: string;
  supportEmail: string;
  abuseEmail: string;
  billingCurrency: string;
  billingProvider: string;
  devLogin: boolean;
  features: {
    signup: boolean;
    webmail: boolean;
    acme: boolean;
  };
}

/** `GET /api/v1/auth/portal/sessions` の 1 件。一覧は `CountedList` で返る。 */
export interface SessionInfo {
  id: string;
  ip: string;
  userAgent: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  active: boolean;
  current: boolean;
}

/* ---------------------------------------------------------------- 組織・契約 */

export interface OrgSummary {
  id: string;
  slug: string;
  displayName: string;
  status: OrganizationStatus;
  billingEmail: string;
  contactName: string | null;
  contactPhone: string | null;
  postalCode: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  country: string;
  taxId: string | null;
  locale: string;
  timezone: string;
  createdAt: string;
  /** 契約が 1 件も無い組織では null。 */
  subscription: {
    id: string;
    status: SubscriptionStatus;
    planCode: string;
    planName: string;
    quantity: number;
    currentPeriodEnd: string;
    trialEndsAt: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  plan: {
    code: string;
    name: string;
    limits: PlanLimits;
    features: PlanFeatures;
  };
  usage: {
    domains: number;
    mailboxes: number;
    aliases: number;
    storageBytes: number;
    sentToday: number;
    sentThisMonth: number;
  };
  /** メールソフト設定に出す接続先（IMAP/SMTP 共通）。 */
  node: {
    region: string;
    mailHostname: string;
  };
}

/* -------------------------------------------------------------- 初期設定 */

/**
 * 初期設定チェックリストの段（`GET /onboarding`）。
 * **順序と可視性はサーバが決める**（権限で見せられない段は返ってこない）。
 */
export type OnboardingStepKey = 'domain' | 'dns' | 'verify' | 'mailbox' | 'mailflow' | 'billing';

export interface OnboardingSnapshot {
  /** 見えている段がすべて完了しているか。true ならカードを出さない。 */
  completed: boolean;
  steps: Array<{ key: OnboardingStepKey; done: boolean }>;
  /** DNS / 所有権の段の導線に使う「いま手当てが要るドメイン」。 */
  domain: {
    id: string;
    name: string;
    status: string;
    dnsStatus: string;
    dnsCheckedAt: string | null;
    lastVerificationError: string | null;
    requiredRecords: number;
    verifiedRecords: number;
    /** 反映待ちの目安（秒）。必須レコードの TTL の最大値。 */
    propagationSeconds: number;
  } | null;
  counts: { domains: number; mailboxes: number };
}

/** プラン上限。`@mail-sv/shared` の `PLAN_CATALOG` と同じキー構成。 */
export interface PlanLimits {
  domainsMax: number;
  mailboxesMax: number;
  aliasesMax: number;
  forwardsPerMailboxMax: number;
  storageBytesTotal: number;
  mailboxQuotaBytesDefault: number;
  mailboxQuotaBytesMax: number;
  messageSizeBytesMax: number;
  sendPerHourMax: number;
  sendPerDayMax: number;
  recipientsPerMessageMax: number;
  imapConnectionsPerUserMax: number;
  retentionDaysTrash: number;
  /**
   * 配信コンテンツの画像・添付の合計容量（バイト、0 = 無制限）。
   * **メールボックスの容量（`storageBytesTotal`）とは別枠**。同じ枠にすると
   * 「バナー画像を置いたせいでメールを受信できなくなった」が起きる。
   */
  contentAssetBytes: number;
  /** 公開申込フォームの作成上限（0 = 無制限）。 */
  signupFormsMax: number;
  /**
   * プライベートメールアドレスの発行上限。
   *
   * **この上限だけ 0 の意味が逆で、`0 = この機能を使えない`。**
   * 他の上限（`domainsMax` など）の 0 は無制限なので、`formatLimit()` や
   * `isAtLimit()` にそのまま渡すと「無制限」と表示され、ライトプランの
   * 契約者に使えない機能の入口を出してしまう。判定は
   * `@mail-sv/shared` の `isPrivateAddressEnabled()` /
   * `privateAddressesRemaining()` を必ず通すこと。
   */
  privateAddressesMax: number;
}

export interface PlanFeatures {
  webmail: boolean;
  catchAll: boolean;
  sieveManage: boolean;
  autoresponder: boolean;
  antivirus: boolean;
  allowUnlimitedQuota: boolean;
  dkimEd25519: boolean;
  dedicatedIp: boolean;
  customMailHostname: boolean;
  apiAccess: boolean;
  auditExport: boolean;
  prioritySupport: boolean;
  /**
   * 配信コンテンツを HTML 直書きで保存できるか。
   * **組織のポリシー（`allowHtmlSource`）との AND** をサーバーが取るので、
   * 画面はこのフラグだけで「使える」と判断しないこと。
   */
  contentHtmlSource: boolean;
  /* ---- 受信者のプライバシー保護（`packages/shared/src/plans.ts`）---- */
  /** リモート画像の代理取得。false のプランでは `proxy` が `block` に落ちる。 */
  imageProxy: boolean;
  imageProxyIpRotation: boolean;
  imageProxyPrefetch: boolean;
  /* ---- Webメールの AI ---- */
  aiAssistant: boolean;
  /** 接続先（プロバイダ・エンドポイント・自分の鍵）を契約者が指定できるか。 */
  aiCustomEndpoint: boolean;
}

/** `GET /portal/members` の 1 件。`id` は membership の ID。 */
export interface Member {
  id: string;
  orgUserId: string;
  email: string;
  displayName: string;
  role: OrgRole;
  status: 'active' | 'invited' | 'disabled';
  invitedAt: string | null;
  acceptedAt: string | null;
  lastLoginAt: string | null;
}

/* -------------------------------------------------------------------- ドメイン */

export interface DomainSummary {
  id: string;
  name: string;
  /** 国際化ドメインの U-label。punycode でなければ null。 */
  unicodeName: string | null;
  status: DomainStatus;
  /** DNS 全体判定。個々のレコードは `/domains/:id/dns-records`。 */
  dnsStatus: DnsStatus;
  dnsCheckedAt: string | null;
  verifiedAt: string | null;
  lastVerificationError: string | null;
  mailHostname: string;
  catchAllMailboxId: string | null;
  inboundEnabled: boolean;
  outboundEnabled: boolean;
  spfPolicy: 'softfail' | 'fail';
  dmarcPolicy: 'none' | 'quarantine' | 'reject';
  dmarcRua: string | null;
  srsEnabled: boolean;
  revision: number;
  mailboxCount: number;
  aliasCount: number;
  storageBytes: number;
  createdAt: string;
}

/** `POST /portal/domains` は 202 + jobId（ドメイン本体は返らない）。 */
export interface DomainCreated {
  jobId: string;
  domainId: string;
}

/** `GET /portal/domains/:id/catch-all`。 */
/**
 * `GET /portal/domains/:id/dmarc`（`assessDmarc()`）。
 *
 * **`blockers` を必ず画面に出すこと。** 「引き上げられません」だけを返さず
 * 何が足りないのかを機械可読で返すのは、画面が「サポートに聞いてください」と
 * しか書けない状態を作らないため（API 側の設計コメント）。
 */
export interface DmarcState {
  policy: 'none' | 'quarantine' | 'reject';
  rua: string | null;
  recommendedNext: 'none' | 'quarantine' | 'reject' | null;
  canRaise: boolean;
  blockers: Array<{ code: string; message: string }>;
  soakDays: number;
  soakRequiredDays: number;
  checks: { spf: boolean; dkim: boolean; dmarcPublished: boolean };
}

/**
 * `GET /portal/domains/:id/certificate`。
 *
 * **行が無いことは異常ではない**（専用ホスト名を使っていない契約では
 * 1 行も作られない）ので、404 ではなく `status: 'not_requested'` が返り、
 * そのときは `certificateId` 以降が欠ける。PEM は返らない。
 */
export interface CertificateState {
  domainId: string;
  hostname: string | null;
  status: string;
  certificateId?: string;
  commonName?: string;
  san?: string[];
  issuer?: string | null;
  challengeType?: string | null;
  notBefore?: string | null;
  notAfter: string | null;
  renewAfter?: string | null;
  daysRemaining: number | null;
  /** 失敗理由は**そのまま見せる**（契約者自身にしか直せない原因が大半）。 */
  lastError: string | null;
  failureCount?: number;
  updatedAt?: string;
}

export interface CatchAll {
  enabled: boolean;
  mailboxId: string | null;
  address: string | null;
}

/* ------------------------------------------------------------ メールボックス */

export interface Mailbox {
  id: string;
  domainId: string;
  domainName: string;
  address: string;
  localPart: string;
  /** 未設定なら null。Input に流すときは `?? ''` すること。 */
  displayName: string | null;
  status: MailboxStatus;
  /** null はプラン既定を継承。0 は無制限（プランが許可する場合のみ）。 */
  quotaBytes: number | null;
  usedBytes: number;
  messageCount: number;
  quotaWarnBp: number;
  imapEnabled: boolean;
  pop3Enabled: boolean;
  submissionEnabled: boolean;
  sieveEnabled: boolean;
  webmailEnabled: boolean;
  spamAction: SpamAction;
  forwardCount: number;
  autoresponderEnabled: boolean;
  lastLoginAt: string | null;
  passwordUpdatedAt: string | null;
  revision: number;
  createdAt: string;
}

/** `POST /portal/mailboxes` のリクエスト。フィールド名は API の zod と一致させること。 */
export interface MailboxCreateRequest {
  domainId: string;
  localPart: string;
  displayName?: string;
  password?: string;
  /** null = プラン既定を継承。0 = 無制限（プランが許す場合のみ）。 */
  quotaBytes?: number | null;
  imapEnabled?: boolean;
  pop3Enabled?: boolean;
  submissionEnabled?: boolean;
  webmailEnabled?: boolean;
}

/** 202 応答。`generatedPassword` はサーバー生成のときだけ 1 回返る。 */
export interface MailboxCreated {
  jobId: string;
  mailboxId: string;
  address: string;
  generatedPassword?: string;
}

/** `GET/PUT /portal/mailboxes/:id/autoresponder`。UI 向けの変換は `lib/autoresponder.ts`。 */
/**
 * `GET /portal/mailboxes/:id/senders` — 差出人 ACL（`sender_acl_entries`）。
 *
 * `sender` は `user@example.jp`（1 件）か `@example.jp`（ドメイン全体）。
 * `autoGenerated` はトリガが作った「自分自身のアドレス」の行で、
 * **削除できない**（消すと自分の名前で送れなくなる。API は 409 を返す）。
 */
export interface SenderAclEntry {
  id: string;
  sender: string;
  autoGenerated: boolean;
  createdAt: string;
}

export interface Autoresponder {
  enabled: boolean;
  subject: string;
  bodyText: string;
  fromDisplayName: string | null;
  startsAt: string | null;
  endsAt: string | null;
  /** 同じ相手へ再送するまでの間隔（秒）。86400 = 1 日 1 回。 */
  repeatIntervalSeconds: number;
  onlyToMyAddresses: boolean;
}

/**
 * `GET /portal/mailboxes/:id/webmail` — ディープリンクは必ずサーバーが組み立てる。
 * v1 は SSO 未実装なので `sso: false` 固定（§7.6）。有効期限も無い。
 */
export interface WebmailLink {
  url: string;
  address: string;
  sso: boolean;
}

/**
 * `POST /portal/mailboxes/:id/webmail/password`（fresh-auth 必須）。
 *
 * **保存されない値。** 要求のたびに KMS から導出し直すもので、DB にも
 * Redis にも監査ログにも入らない。画面も再表示できないことを明示する。
 * メールソフト（IMAP/SMTP）のパスワードとは別物。
 */
export interface WebmailPassword {
  address: string;
  password: string;
  credentialVersion: number;
  url: string;
  /** 画面にそのまま出す注意書き（サーバが文面を持つ）。 */
  note: string;
}

/* ------------------------------------------------------ エイリアス・転送 */

export interface Alias {
  id: string;
  domainId: string;
  domainName: string;
  address: string;
  localPart: string;
  /** 配送先メールボックスの ID。 */
  destinationMailboxIds: string[];
  /** 同じ配送先をアドレスで表したもの（表示用。削除済みは含まれない）。 */
  destinations: string[];
  enabled: boolean;
  description: string | null;
  createdAt: string;
}

/* ------------------------------------------ プライベートメールアドレス */

/**
 * `GET /portal/private-addresses`。
 *
 * `CursorPage<PrivateAddressView>` に `summary` が付いた形。**`summary` は
 * ページの合計ではなく組織全体の値**なので、ページを繰っても数字が変わらない
 * （漏洩元の判断に使う数字がページ送りで動くと、そもそも判断に使えない）。
 *
 * 行と統計の型は `@mail-sv/shared` の `PrivateAddressView` /
 * `PrivateAddressSummary` が唯一の正。ここで再定義しないこと。
 */
export interface PrivateAddressPage {
  items: PrivateAddressView[];
  nextCursor: string | null;
  summary: PrivateAddressSummary;
}

/**
 * `POST /portal/private-addresses` の 201。
 * 発行した行に、ノードへの反映ジョブ ID（積めなかったときは null）が付く。
 */
export type PrivateAddressCreated = PrivateAddressView & { jobId: string | null };

/** `disable` / `enable` / `DELETE` の 202。 */
export interface PrivateAddressAccepted {
  status?: string;
  jobId: string | null;
}

export interface Forwarder {
  id: string;
  mailboxId: string;
  mailboxAddress: string;
  destination: string;
  /** 転送元にもコピーを残すか。 */
  keepCopy: boolean;
  enabled: boolean;
  /** SRS が有効なノードでは外部転送でも SPF が壊れない（表示用）。 */
  srsEnabled: boolean;
  /** 同意確認が済むまで false。false の間は配送に効いていない。 */
  verified: boolean;
  verifiedAt: string | null;
  createdAt: string;
}

/* ------------------------------------------------------------ 迷惑メール */

/** `GET/PUT /portal/domains/:id/spam`。実体は `domains.settings.spam`。 */
export interface SpamPolicy {
  domainId: string;
  enabled: boolean;
  /** 判定強度。弱 = しきい値が高い（判定されにくい）。 */
  level: 'low' | 'medium' | 'high';
  action: SpamAction;
  /** 1 行 1 件のアドレス or ドメイン。 */
  allowList: string[];
  denyList: string[];
}

export interface SpamStats {
  from: string;
  to: string;
  /** 直近 30 日。日付の抜けはサーバー側で 0 埋め済み。 */
  days: Array<{ date: string; received: number; spam: number; virus: number }>;
}

/* ---------------------------------------------------------------- 使用量 */

/** `GET /portal/usage`。画面向けの整形は `lib/usage.ts`。 */
export interface UsageResponse {
  period: { from: string; to: string };
  current: {
    domains: number;
    mailboxes: number;
    aliases: number;
    storageBytes: number;
    sentToday: number;
    sentThisMonth: number;
  };
  limits: PlanLimits;
  perMailbox: Array<{
    mailboxId: string;
    address: string;
    usedBytes: number;
    /** プラン既定の継承はサーバー側で解決済み。0 は無制限。 */
    quotaBytes: number;
  }>;
  /** 日次の生データ。metric ごとに 1 行。 */
  series: Array<{ date: string; metric: string; value: number; aggregation: string }>;
}

/* -------------------------------------------------------------------- ログ */

/**
 * `GET /portal/logs/mail`。
 *
 * **1 通ごとの配送ログではない**。配送ログの実体はノード DB（`delivery_events`）
 * にあり、コントロールプレーンにはノードが上げた運用イベント
 * （`agent.event.*`）しか無い。差出人・宛先・件名・配送結果は CP に存在しないので、
 * それらを画面に出す前提で型を書いてはいけない（以前の `MailLogEntry` がそれで、
 * 全項目 undefined になっていた）。
 */
export interface MailEventLogEntry {
  id: string;
  occurredAt: string;
  /** `@mail-sv/shared` の `AGENT_EVENT_KIND`。 */
  kind: string;
  severity: 'info' | 'warning' | 'critical';
  sourceIp: string | null;
  /** 同種イベントの畳み込み件数。 */
  count: number;
  detail: string | null;
  /** ノード側で既に遮断済みか。 */
  blockedLocally: boolean;
  target: string | null;
}

/**
 * `GET /portal/logs/delivery` — 1 通ごとの配送記録（`mail_log_entries`）。
 *
 * **件名も本文も無い**（CLAUDE.md §7-9 の不変条件）。返るのは封筒に
 * 書いてあることと配送結果だけ。`GET /logs/mail`（ノード側の
 * セキュリティイベント）とは別の情報なので混ぜないこと。
 */
export interface DeliveryLogEntry {
  id: string;
  occurredAt: string;
  direction: 'inbound' | 'outbound';
  /** エンベロープの差出人。バウンス通知など空の場合がある。 */
  sender: string | null;
  recipient: string;
  disposition: 'delivered' | 'bounced' | 'deferred' | 'rejected';
  severity: string;
  /** 相手 MTA の応答コード（`550` など）。 */
  smtpCode: string | null;
  /** DSN のステータス（`5.1.1` など）。 */
  dsnStatus: string | null;
  diagnostic: string | null;
  mailboxId: string | null;
  campaignId: string | null;
}

/**
 * `GET /portal/logs/audit` — 自組織の操作履歴。
 *
 * **`before_state` / `after_state` は返らない**（他の利用者の属性や
 * ノードの内部 ID が混ざるため）。`actor` は組織ユーザーのときだけ
 * メールアドレスが入り、運営スタッフの操作では null になる
 * （「運営者が触った」ことが分かれば十分、という API 側の判断）。
 */
export interface AuditLogEntry {
  id: string;
  occurredAt: string;
  action: string;
  actorType: string;
  actor: string | null;
  targetType: string | null;
  targetId: string | null;
  success: boolean;
  /** 代理ログイン中の操作か。契約者が自分で見分けられるようにする。 */
  viaImpersonation: boolean;
}

/** `GET /portal/logs/auth` — 監査ログのうち `auth.*` のもの。 */
export interface AuthLogEntry {
  id: string;
  occurredAt: string;
  /** `auth.login` / `auth.password_changed` など。 */
  action: string;
  actorId: string | null;
  /** 組織ユーザー以外（運営者・system）の操作では null。 */
  actorEmail: string | null;
  ip: string | null;
  userAgent: string | null;
  success: boolean;
}

/* -------------------------------------------------------------- セキュリティ */

export interface SecuritySummary {
  domains: Array<{
    id: string;
    name: string;
    dnsStatus: DnsStatus;
    status: DomainStatus;
    outboundAllowed: boolean;
  }>;
  mailboxesByStatus: Partial<Record<MailboxStatus, number>>;
  authFailuresLast7Days: number;
  spamBlocked30d: number;
  virusBlocked30d: number;
  authFailures24h: number;
  blockedIps: number;
  /** 直近のログイン履歴（portal ログイン）。 */
  recentLogins: Array<{ at: string; ip: string; userAgent: string; result: 'success' | 'failure' }>;
}

export interface SendingLimits {
  limits: { perHour: number; perDay: number; recipientsPerMessage: number };
  sentToday: number;
  perDayMax: number;
  sentThisHour: number;
  perHourMax: number;
  recipientsPerMessageMax: number;
  messageSizeBytesMax: number;
  /** 直近 24 時間のメールボックス別内訳。 */
  last24h: Array<{
    mailboxId: string;
    address: string;
    sent: number;
    recipients: number;
    rejected: number;
  }>;
}

/**
 * 自組織に効いている IP 遮断（`GET /portal/security/ip-blocks`）。
 *
 * **`scope='global'` の行は返らない。** グローバル遮断の対象 CIDR を
 * 契約者に見せると「どのネットワークが遮断対象か」を外から探れるため、
 * API と RLS の両方で塞いである。
 */
export interface IpBlockEntry {
  id: string;
  cidr: string;
  action: string;
  protocols: string[];
  reason: string;
  source: string;
  hitCount: number;
  expiresAt: string | null;
  createdAt: string;
}

/** `POST /portal/security/ip-blocks/:ruleId/appeal`（201）。 */
export interface IpBlockAppealCreated {
  ticketId: string;
  reference: string;
  firstResponseDueAt: string;
}

/* ---------------------------------------------------------- Webメールの AI */

/**
 * `GET /portal/ai/policy`。
 *
 * **`requested`（希望）と `effective`（実効）が両方返る**のが要点。
 * 画面はこの 2 つを並べて「プランで削られた」「運営に固定された」を
 * 見せる責任がある（`apps/api/src/routes/portal/ai.ts` の冒頭）。
 */
export interface AiPolicyView {
  plan: {
    code: string;
    aiAssistant: boolean;
    aiCustomEndpoint: boolean;
    aiMonthlyTokens: number;
  };
  platform: { provider: string; model: string; allowedProviders: string[] };
  requested: OrgAiPolicy;
  effective: OrgAiPolicy;
  configured: boolean;
  lockedByOps: boolean;
  lockedAt: string | null;
  opsNote: string | null;
  sync: { state: string; syncedAt: string | null; error: string | null };
  /** **保存はできるが Webメール側で強制できない**指定。隠さずに出す。 */
  notEnforced: { fields: string[]; features: string[]; note: string };
  credential: { hasKey: boolean; fingerprint: string | null };
  usage: {
    month: string;
    calls: number;
    failedCalls: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    cap: number;
    overCap: boolean;
  };
  cappedAt: string | null;
  /** `reason` が入っていれば AI は止まっている。**必ず画面に出す。** */
  routing: {
    preferredRegion: string;
    requireResidency: string;
    region: string | null;
    dataResidency: string | null;
    reason: string | null;
    nodeRegion: string | null;
    canChooseResidency: boolean;
  };
  updatedAt: string | null;
}

/** `GET /portal/ai/usage`。**利用者単位の内訳は返らない**（台帳を持たない設計）。 */
export interface AiUsageView {
  month: string;
  total: {
    month: string;
    calls: number;
    failedCalls: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  items: Array<{
    usageDate: string;
    provider: string;
    model: string;
    task: string;
    calls: number;
    failedCalls: number;
    inputTokens: number;
    outputTokens: number;
  }>;
}

/* ------------------------------------------------- 受信者のプライバシー保護 */

/**
 * `GET /portal/privacy/remote-images`。
 *
 * `configured` は**契約者が保存した値**（未保存なら null）、`effective` は
 * **プランを当てた後に実際に効いている値**。両方返るのは「設定したのに
 * 効いていない」を画面で説明できるようにするため。
 */
export interface PrivacyPolicyView {
  configured:
    | (OrgPrivacyPolicy & { revision: number; updatedAt: string })
    | null;
  effective: OrgPrivacyPolicy;
  features: {
    imageProxy: boolean;
    imageProxyIpRotation: boolean;
    imageProxyPrefetch: boolean;
  };
  planCode: string;
}

/** `GET /portal/privacy/image-proxy/hosts`。**受信者は 1 人も出てこない。** */
export interface ImageProxyHostStats {
  days: number;
  hosts: Array<{
    sourceHost: string;
    verdict: string;
    fetches: number;
    bytes: number;
  }>;
}

/* ------------------------------------------------------------ 請求・プラン */

/**
 * 振込先の案内（`bankTransferInfo`）。
 *
 * **null は「振込先が無い」ではなく「まだ設定されていない」。**
 * 画面は null のときに空欄で済ませず、問い合わせ導線を出す
 * （`apps/api/src/services/bank-transfer.ts` の設計コメントと対にしてある）。
 */
export interface BankTransferInfo {
  lines: string[];
  note: string;
}

/** 請求書の宛先（`organizations` の該当列）。`missing` が空でなければ未完成。 */
export interface BillingProfile {
  displayName: string;
  billingEmail: string;
  postalCode: string | null;
  addressLine1: string | null;
  contactName: string | null;
  contactPhone: string | null;
  taxId: string | null;
  complete: boolean;
  /** 未入力の項目名（`postalCode` / `addressLine1` / `contactName`）。 */
  missing: string[];
}

/** 当月の請求見込み。**確定値ではない**（`provisional`）。 */
export interface UpcomingInvoice {
  /** `yyyyMM`（区切りなし）。 */
  yearMonth: string;
  periodStart: string;
  periodEnd: string;
  baseAmount: number;
  overageAmount: number;
  creditAmount: number;
  subtotal: number;
  taxAmount: number;
  total: number;
  taxRateBp: number;
  /** 未確定の日を含むか（＝これから動き得るか）。 */
  provisional: boolean;
  /** 後払い→前払いの移行月（当月分と翌月分が 1 通に並ぶ）か。 */
  transitional: boolean;
  lines: Array<{ kind: string; description: string; amount: number }>;
  overage: {
    mailbox: OverageEstimateLine;
    storage: OverageEstimateLine;
  };
}

export interface OverageEstimateLine {
  included: number;
  peak: number;
  excess: number;
  unitPrice: number;
  amount: number;
  metered: boolean;
}

export interface Subscription {
  id: string;
  status: SubscriptionStatus;
  planCode: string;
  planName: string;
  quantity: number;
  billingPeriod: 'monthly' | 'yearly';
  /** `billingPeriod` に対応した金額（**税抜**）。表示は `priceLabel` を使う。 */
  price: number;
  /**
   * 税込額と表示用の文字列。
   *
   * **画面で 1.1 を掛けない。** 税率は `packages/shared` の 1 か所で決まり、
   * 軽減税率・改定のたびに画面が追随できないため、サーバが作った
   * `priceLabel`（「1,650 円（税込・税抜 1,500 円）」）をそのまま出す。
   */
  priceIncludingTax: number;
  priceLabel: string;
  /** カタログの現行価格（税抜）。据え置き中は `price` と食い違う。 */
  catalogPrice: number;
  /** 契約時価格の据え置きが効いているか。 */
  priceGrandfathered: boolean;
  /** 予告済みの料金改定。無ければ null。 */
  pendingPriceChange: {
    effectiveAt: string;
    price: number;
    priceLabel: string;
    reason: string | null;
  } | null;
  currency: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  /** 解約予約中は null。 */
  nextInvoiceAt: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  provider: string;
  limits: PlanLimits;
  features: PlanFeatures;
  usage: { domains: number; mailboxes: number; aliases: number };
  /** 未登録でも null ではなく `kind:'none'` が返る。 */
  paymentMethod: {
    kind: 'card' | 'invoice' | 'none';
    brand: string | null;
    last4: string | null;
    expMonth: number | null;
    expYear: number | null;
  };
  /**
   * この環境で使える決済事業者（`stripe` / `manual` など）。
   * **`manual` のときにカード登録ボタンを出すと必ず 400 になる**ので、
   * 画面はこの値でボタンを出し分ける。
   */
  billingProvider: string;
  bankTransferInfo: BankTransferInfo | null;
  billingProfile: BillingProfile;
  upcomingInvoice: UpcomingInvoice | null;
  /**
   * 解約手続きの状態。**画面がここを読まないと、契約者は
   * 「いつデータが消えるのか」「撤回できるのか」を知る手段が無い。**
   */
  termination: {
    organizationStatus: string;
    /** メールデータの削除予定日時。解約手続き前は null。 */
    terminateAfter: string | null;
    /** 宛先名簿など個人データの消去予定日時。 */
    purgeAfter: string | null;
    /** `POST /billing/cancel/revoke` で戻せるか。 */
    revocable: boolean;
  };
}

/** `POST /billing/cancel` / `/cancel/immediate` の応答。 */
export interface CancelResult {
  ok: boolean;
  immediate: boolean;
  /** 効力発生日時（期間末解約なら現期間の終わり）。 */
  effectiveAt: string;
  revocable: boolean;
}

export interface Invoice {
  id: string;
  number: string;
  status: InvoiceStatus;
  /** `YYYY-MM-DD`（date 列なので時刻は付かない）。 */
  periodStart: string;
  periodEnd: string;
  currency: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  hasPdf: boolean;
  /** 赤伝（適格返還請求書）。請求ではないので画面で区別する。 */
  isCreditNote: boolean;
}

/**
 * `GET /portal/invoices/:invoiceId` — 明細つきの 1 通。
 *
 * **PDF が無くても内訳を読める唯一の経路。** PDF の生成は運営側の env
 * が揃うまで動かないので、既定構成ではこちらしか存在しない。
 *
 * 金額は**サーバが計算した値をそのまま出す**（税額は「税率ごとに 1 回だけ」
 * 丸めてある。画面で掛け算し直すと小計 + 消費税 ≠ 合計になる）。
 * `issuerRegistrationNo` が null なら**適格請求書ではない**。
 */
export interface InvoiceDetail {
  id: string;
  number: string;
  status: InvoiceStatus;
  periodStart: string;
  periodEnd: string;
  currency: string;
  subtotal: number;
  taxAmount: number;
  /** ベーシスポイント（10% = 1000）。 */
  taxRateBp: number;
  total: number;
  issuerRegistrationNo: string | null;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  lines: Array<{
    lineNo: number;
    kind: string;
    description: string;
    quantity: number;
    unitPrice: number;
    amount: number;
  }>;
}

/* -------------------------------------------- ブランディング・ホスト名 */

/** `GET/PUT /portal/branding`。ロゴは URL ではなく**公開バケットの key** から作られる。 */
export interface Branding {
  brandName: string | null;
  tagline: string | null;
  primaryColor: string | null;
  supportUrl: string | null;
  footerHtml: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  updatedAt: string | null;
  /** 1 つでも設定済みか。false なら Webメールは既定ブランドで出る。 */
  configured: boolean;
}

/** `POST /portal/branding/logo` の 201。**本文は API を通さず S3 へ直接 PUT する。** */
export interface LogoUploadTicket {
  objectKey: string;
  upload: {
    url: string;
    method: string;
    headers: Record<string, string>;
    expiresAt: string;
  };
}

export interface CustomHostname {
  id: string;
  kind: 'webmail' | 'link';
  hostname: string;
  /** **`active` にできるのは検証ジョブだけ**（契約者は状態を変えられない）。 */
  status: 'pending' | 'verifying' | 'active' | 'failed';
  domainId: string;
  dnsVerifiedAt: string | null;
  lastError: string | null;
  createdAt: string;
  /** 契約者が DNS に入れる値。**画面で組み立てない**（サーバが正）。 */
  expectedDns: { type: string; name: string; value: string | null };
}

/* ------------------------------------------------------------ 書き出し */

/**
 * `POST /portal/exports` で**申請できる**内容。`audit` だけはプラン機能
 * （`auditExport`）で制限される。
 */
export type ExportKind = 'audit' | 'usage' | 'mailboxes' | 'invoices';

/**
 * `GET /portal/exports` に**出てくる**種別。
 *
 * 申請できる 4 種のほかに、別経路で作られるものが混ざる:
 *   - `maildir` … `POST /mailboxes/:id/export`（メール本文の mbox）
 *   - `contacts` … `GET /contacts/vcard`（同期生成の持ち出し記録）
 * **`ExportKind` と同一視しない。** 同じ型にすると一覧の訳語表から漏れて
 * 生の識別子（`maildir`）が画面に出る。
 */
export type ExportJobKind = ExportKind | 'maildir' | 'contacts' | (string & {});

/** `GET /portal/exports` の 1 件。 */
export interface ExportJob {
  id: string;
  kind: ExportJobKind;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  /** mbox / vcf は CSV でも JSON でもない。 */
  format: string;
  rowCount: number | null;
  byteSize: number | null;
  /** 生成物の失効時刻。**7 日で消える**ので画面に必ず出す。 */
  expiresAt: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/** `GET /portal/invoices/:id/pdf` の応答。**PDF 本体ではなく署名付き URL**。 */
export interface InvoicePdfLink {
  objectKey: string;
  number: string;
  bucket: string;
  url: string;
  expiresAt: string;
}

export interface PublicPlan {
  code: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  limits: PlanLimits;
  features: PlanFeatures;
}

/* ------------------------------------------------------------ API トークン */

export interface ApiToken {
  id: string;
  name: string;
  /** 先頭 12 文字だけ。全体は作成時にしか返らない。 */
  prefix: string;
  /** 権限コードの配列。API 側の列名は `scopes`。 */
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** `POST /portal/api-tokens` の 201 応答。`token` は**この 1 回しか返らない**。 */
export interface ApiTokenCreated {
  id: string;
  token: string;
  prefix: string;
}

/* ------------------------------------------------------------ DNS レコード */

/** `lib/dns-records.ts` が再利用する判定値の別名。 */
export type { DnsCheckResult };

/* -------------------------------------------------------- 送信元ストリーム */

/**
 * `GET /portal/sending-streams` の 1 件（`toView()` の写し）。
 *
 * **`status` を画面から書き換える手段は無い。** `verified` に上げてよいのは
 * `dns-verify` キューだけで、この面から出せるのは検証依頼（`verifying`）まで。
 * 自己申告で verified にできると、SPF/DKIM/DMARC が揃っていないドメインから
 * 一斉配信が出て、同じノードの他契約者まで巻き添えにする。
 */
export interface SendingStream {
  id: string;
  kind: SendingStreamKind;
  domainId: string;
  /** 表示用のドメイン名。API が join して返す（ID だけでは画面が組めない）。 */
  domain: string | null;
  mailboxId: string;
  /** 差出人アドレス（`localPart@domain`）。 */
  fromAddress: string | null;
  fromDisplayName: string | null;
  bounceLocalPart: string;
  /** VERP の受け口（`bounceLocalPart@domain`）。 */
  bounceAddress: string | null;
  /** 0 = プラン既定に従う。 */
  dailyCap: number;
  status: SendingStreamStatus;
  verifiedAt: string | null;
  bounceProbeAt: string | null;
  /** 検証が失敗した理由。**画面に必ず出す**（出さないと直しようがない）。 */
  lastFailureReason: string | null;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

/* -------------------------------------------------------------- 申込フォーム */

/**
 * `GET /portal/signup-forms` の 1 件（`toFormView()` の写し）。
 *
 * **`publicUrl` は版がある（`currentVersionId != null`）ときだけ入る。**
 * 版 = 凍結された同意文言なので、版の無いフォームは公開できない。
 */
export interface SignupForm {
  id: string;
  listId: string;
  slug: string;
  name: string;
  status: SignupFormStatus;
  currentVersionId: string | null;
  currentVersion: number | null;
  /** 二重オプトイン。**既定は true**（確認メールを踏むまで購読状態にしない）。 */
  doubleOptin: boolean;
  /** **空配列 = 自オリジンのみ。** `*` に相当する値は存在しない（D31）。 */
  allowedOrigins: string[];
  challengeMode: SignupChallengeMode;
  dailySubmitCap: number;
  accentColor: string | null;
  archivedAt: string | null;
  publicUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

/** フォームに置ける入力欄。**自由な HTML は置けない**（kind は閉じた列挙）。 */
export interface SignupFormFieldView {
  kind: 'email' | 'text' | 'select' | 'checkbox';
  key: string;
  label: string;
  required: boolean;
  options: string[];
  placeholder?: string;
}

/** 特定電子メール法の表示義務を版に焼き付けたもの。 */
export interface LegalFooterSnapshotView {
  senderName: string;
  address: string;
  unsubscribeContact: string;
  complaintContact: string;
}

/** 版。**公開後は 1 バイトも変えられない**（DB トリガが UPDATE / DELETE を拒否する）。 */
export interface SignupFormVersion {
  id: string;
  formId: string;
  version: number;
  fields: SignupFormFieldView[];
  consentText: string;
  consentTextSha256: string;
  privacyUrl: string | null;
  legalFooterSnapshot: LegalFooterSnapshotView;
  redirectUrl: string | null;
  publishedAt: string | null;
  createdAt: string;
}

export interface SignupFormDetail extends SignupForm {
  versions: SignupFormVersion[];
}

/**
 * 申込の受付状況。**メールアドレスは返らない。**
 * 未確認の第三者のアドレスを契約者に見せると、list-bombing が
 * そのままアドレス収集の手段になる。
 */
export interface SignupSubmission {
  id: string;
  state: string;
  formVersion: number;
  submittedAt: string;
  confirmSentAt: string | null;
  confirmedAt: string | null;
  expiresAt: string;
}

/* ---------------------------------------------------------------- 通知など */

/** `GET /portal/notifications` の 1 件。 */
export interface AppNotification {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  category: string;
  title: string;
  bodyMd: string;
  targetType: string | null;
  targetId: string | null;
  readAt: string | null;
  createdAt: string;
}

/** `GET /portal/incidents` の 1 件。**公開文面だけが返る**（内部の調査メモは来ない）。 */
export interface CustomerIncident {
  id: string;
  reference: string;
  title: string;
  severity: string;
  status: string;
  publicMessage: string | null;
  startedAt: string;
  resolvedAt: string | null;
}

/** `GET /portal/maintenance-windows` の 1 件。告知済みの計画メンテナンスのみ。 */
export interface MaintenanceWindow {
  id: string;
  title: string;
  bodyMd: string | null;
  startsAt: string;
  endsAt: string;
  announcedAt: string | null;
}

/* ------------------------------------------------------------ 問い合わせ */

export interface SupportTicketSummary {
  id: string;
  reference: string;
  kind: string;
  status: string;
  priority: string;
  subject: string;
  /** 一次応答の期限。**約束したものを隠さない**ので契約者にも出す。 */
  firstResponseDueAt: string | null;
  firstRespondedAt: string | null;
  lastMessageAt: string;
  createdAt: string;
}

export interface SupportTicketMessage {
  id: string;
  authorType: string;
  authorLabel: string;
  bodyMd: string;
  createdAt: string;
}

export interface SupportTicketDetail extends Omit<SupportTicketSummary, 'lastMessageAt'> {
  resolvedAt: string | null;
  closedAt: string | null;
  messages: SupportTicketMessage[];
}

/** `GET /portal/privacy-requests` の 1 件（開示・訂正・削除等の請求）。 */
export interface PrivacyRequest {
  id: string;
  reference: string;
  kind: string;
  status: string;
  receivedAt: string;
  dueAt: string;
  completedAt: string | null;
  ticketId: string | null;
}

/* ---------------------------------------------------------------- 法定文書の同意 */

/** `GET /portal/consents` が返す文書の参照。本文は `/legal/:slug` にある。 */
export interface ConsentDocumentRef {
  /** `terms` / `privacy`。将来増えても画面が落ちないよう string で受ける。 */
  kind: string;
  title: string;
  version: string;
}

/**
 * `GET /portal/consents`。
 *
 * `pending` が空でなければ**現行版に同意していない**。書き込みはブロックされない
 * （同意できないので解約したい、を塞がないため）ので、画面も導線を出すだけにする。
 */
export interface ConsentsView {
  current: ConsentDocumentRef[];
  pending: ConsentDocumentRef[];
  accepted: Array<{ kind: string; version: string; acceptedAt: string }>;
}

/** `POST /portal/consents` の応答。`pending` は記録後に残っているもの。 */
export interface ConsentsRecorded {
  ok: boolean;
  recorded: number;
  pending: ConsentDocumentRef[];
}

/* ------------------------------------------------------------------ SLA */

/** `GET /portal/billing/sla` のクレジット 1 件。 */
export interface SlaCredit {
  id: string;
  status: string;
  periodStart: string;
  periodEnd: string;
  uptimeBp: number | null;
  creditBp: number;
  amount: number;
  /** 次回以降へ繰り越す残額。0 なら充当済み。 */
  remainingAmount: number;
  reason: string | null;
  source: string;
  applied: boolean;
  createdAt: string;
}

/**
 * `GET /portal/billing/sla`。
 *
 * `guaranteeBp` が 0 のプランには**サービスクレジットの定めが無い**
 * （SLA は努力目標としての掲示）。画面は申告の導線を出さない。
 */
export interface SlaView {
  /** 保証稼働率（basis point。99.9% = 9990）。0 なら定めなし。 */
  guaranteeBp: number;
  tiers: Array<{ belowBp: number; creditBp: number }>;
  /** 申告できる対象月（`yyyyMM`）。期限切れは含まれない。 */
  claimableMonths: string[];
  credits: SlaCredit[];
}

/* ------------------------------------------------------ 組織のセキュリティ方針 */

export interface MemberMfaStatus {
  orgUserId: string;
  email: string;
  displayName: string;
  roleCode: string;
  mfaEnabled: boolean;
}

/** `GET /portal/security/mfa-policy`。 */
export interface MfaPolicy {
  mfaRequired: boolean;
  mfaRequiredAt: string | null;
  updatedAt: string | null;
  members: MemberMfaStatus[];
  /** 有効化した瞬間に締め出される人数。0 でなくても有効化はできる。 */
  membersWithoutMfa: number;
}

/* ------------------------------------------------------------ 設定情報シート */

/** 設定情報シートの 1 行（`GET /portal/mailboxes/:id/setup-sheet`）。 */
export interface SetupSheetConnection {
  purpose: string;
  protocol: 'IMAP' | 'POP3' | 'SMTP' | 'ManageSieve';
  host: string;
  port: number;
  encryption: 'SSL/TLS' | 'STARTTLS';
  recommended: boolean;
  note: string;
}

/**
 * メールアドレス 1 つぶんの設定情報シート。
 *
 * **パスワードは含まれない**（API がそもそも返さない）。紙に印字するのは
 * 「管理者が設定した値」という説明だけで、値そのものは載せない。
 */
export interface SetupSheet {
  mailboxId: string;
  address: string;
  displayName: string | null;
  domainName: string;
  host: string;
  /** ログイン ID。必ずメールアドレス全体。 */
  username: string;
  connections: SetupSheetConnection[];
  webmailUrl: string | null;
  /** 0 は無制限。 */
  quotaBytes: number;
  usedBytes: number;
  /** 自動設定（Outlook / Thunderbird）が実際に効く状態か。 */
  autoconfigReady: boolean;
  issuedAt: string;
}
